import React, { useState } from 'react';
import { Copy, ExternalLink, Loader2, Mail, MessageCircle } from 'lucide-react';
import { api, Staff, Store, isLineEnabled } from '../api';
import Modal from './ui/Modal';
import { useToast } from '../contexts/ToastContext';

interface StaffLoginStatusProps {
  staff: Staff;
  stores: Store[];
  onChanged?: () => void;
  // 一覧の表示（バッジ＋小さなボタン）か、編集画面の表示（説明付きの枠）か。
  variant?: 'cell' | 'panel';
}

// スタッフのログイン手段の状態。所属店舗（先頭）がLINEを使う運用かどうかで、
// 「LINE連携」か「ID・パスワードのアカウント」かを切り替えて表示する。
export const getLoginStatus = (staff: Staff, store: Store | null | undefined) => {
  if (isLineEnabled(store)) {
    if (staff.lineStatus === 'connected') return { label: 'LINE連携済み', tone: 'ok' as const, done: true };
    if (staff.lineStatus === 'invited') return { label: 'LINE招待中', tone: 'wait' as const, done: false };
    return { label: 'LINE未連携', tone: 'none' as const, done: false };
  }
  if (staff.accountStatus === 'active') return { label: 'ログイン可', tone: 'ok' as const, done: true };
  if (staff.accountStatus === 'invited') return { label: '招待中', tone: 'wait' as const, done: false };
  return { label: '未招待', tone: 'none' as const, done: false };
};

const TONE_CLASS = {
  ok: 'text-green-700 bg-green-50 border-green-200',
  wait: 'text-orange-600 bg-orange-50 border-orange-200',
  none: 'text-slate-500 bg-slate-50 border-slate-200',
};

const StaffLoginStatus: React.FC<StaffLoginStatusProps> = ({ staff, stores, onChanged, variant = 'cell' }) => {
  const { showToast } = useToast();
  const store = stores.find(s => s.id === staff.stores?.[0]) || null;
  const lineEnabled = isLineEnabled(store);
  const status = getLoginStatus(staff, store);
  const [isWorking, setIsWorking] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const handleInvite = async () => {
    setIsWorking(true);
    try {
      const url = await api.generateInviteToken(staff.id);
      if (!lineEnabled) {
        if (!staff.email) {
          showToast('メールアドレスを登録してから招待してください。', 'error');
          return;
        }
        await api.sendInviteEmail({ email: staff.email, staffName: `${staff.lastName} ${staff.firstName}`, inviteUrl: url, storeName: store?.name || '店舗' });
      }
      setInviteUrl(url);
      onChanged?.();
    } catch (e) {
      console.error(e);
      showToast('招待URLの発行に失敗しました。', 'error');
    } finally {
      setIsWorking(false);
    }
  };

  const actionLabel = lineEnabled ? 'LINE招待URL' : (staff.accountStatus === 'invited' ? '招待メール再送' : '招待メール送信');
  const ActionIcon = lineEnabled ? MessageCircle : Mail;

  const actionButton = !status.done && (
    <button type="button" onClick={handleInvite} disabled={isWorking}
      className={variant === 'cell'
        ? 'text-[10px] font-bold text-blue-600 bg-white border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-50 flex items-center gap-1 whitespace-nowrap'
        : 'px-4 py-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50 shadow-sm whitespace-nowrap'}>
      {isWorking ? <Loader2 size={variant === 'cell' ? 10 : 14} className="animate-spin" /> : <ActionIcon size={variant === 'cell' ? 10 : 14} />}
      {actionLabel}
    </button>
  );

  return (
    <>
      {variant === 'cell' ? (
        <div className="flex flex-col items-center gap-1" data-testid={`login-status-${staff.id}`}>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border whitespace-nowrap ${TONE_CLASS[status.tone]}`}>{status.label}</span>
          {actionButton}
        </div>
      ) : (
        <div className="flex flex-wrap gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">{lineEnabled ? 'LINE連携' : 'ログインアカウント（ID・パスワード）'}</p>
            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${TONE_CLASS[status.tone]}`}>{status.label}</span>
          </div>
          {actionButton}
        </div>
      )}

      <Modal isOpen={!!inviteUrl} onClose={() => setInviteUrl(null)} title={lineEnabled ? 'LINE連携の招待URL' : '招待メールを送信しました。'} maxWidth="max-w-md">
        {inviteUrl && (
          <div className="space-y-5 pt-2">
            <p className="text-sm text-slate-600 leading-relaxed">
              {lineEnabled
                ? `${staff.lastName} ${staff.firstName}さんに、このURLまたはQRコードを送ってください。スマートフォンで開くとLINEとの連携が始まります。`
                : `${staff.lastName} ${staff.firstName}さん（${staff.email}）に招待メールを送りました。メールのURLからパスワードを設定すると、ログインできるようになります。`}
            </p>
            <div className="flex justify-center">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(inviteUrl)}`} alt="招待URLのQRコード" className="w-40 h-40 border-4 border-slate-100 rounded-xl" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-500 break-all">{inviteUrl}</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { navigator.clipboard?.writeText(inviteUrl).catch(() => {}); showToast('URLをコピーしました。'); }} className="py-2.5 bg-slate-800 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-black"><Copy size={14} />URLをコピー</button>
              <a href={inviteUrl} target="_blank" rel="noopener noreferrer" className="py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-slate-50"><ExternalLink size={14} />URLを開く</a>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StaffLoginStatus;
